import { catchError, map, share } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CategoryType } from './static-data/category-type';
import { CATEGORYTYPES } from './static-data/';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import { Category } from './category';
import { FeatureFlagsService } from './feature-flags.service';
import { FormattedCategories } from '../access-controls/access-overview/access-overview.model';

@Injectable({ providedIn: 'root' })
export class CategoriesService {
  path: string = '/api';
  standardCategories: Category[] | null = null;
  standardCategoryOptions: any = null;
  constructor(private apiService: ApiService, private featureFlagsService: FeatureFlagsService) { }

  private observable1: Observable<Category[]> | null = null;
  private observable2: Observable<any> | null = null;
  private categoryTypes = CATEGORYTYPES;

  getStandardCategories(): Observable<Category[]> {
    if (this.standardCategories) {
      return of(this.standardCategories);
    } else if (this.observable1) {
      return this.observable1;
    } else {
      this.observable1 = this.apiService.get<Category[]>(`${this.path}/standard_categories`).pipe(
        catchError(() => of([])),
        map((response: Category[]) => {
          this.observable1 = null;
          this.standardCategories = response;
          return this.standardCategories;
        }),
        share(),
      );
      return this.observable1;
    }
  }

  getCommunityParticipantCategories(): Observable<string[]> {
    return of(
      [
        {
          id: 'schedule-route',
          name: 'Schedule/route',
          type_id: 6,
        },
        {
          id: 'brand',
          name: 'Brand',
          type_id: 6,
        },
        {
          id: 'promotion',
          name: 'Promotion',
          type_id: 6,
        },
      ].map(item => item.id),
    );
  }

  getStandardCategoryOptions() {
    if (this.standardCategoryOptions) {
      return of(this.standardCategoryOptions);
    } else if (this.observable2) {
      return this.observable2;
    } else {
      this.observable2 = this.apiService.get(`${this.path}/standard_category_options`).pipe(
        map((response: any) => {
          this.observable2 = null;
          this.standardCategoryOptions = response;
          return this.standardCategoryOptions;
        }),
        share(),
      );
      return this.observable2;
    }
  }

  getCategoriesOrderByAirline(airlineId: number) {
    return this.apiService
      .getParams(`${this.path}/category_orderings`, {
        airline_id: airlineId,
      });
  }

  getCategoryTypes(): CategoryType[] {
    return CATEGORYTYPES;
  }

  getStandardCategoriesByType(categoryType: string): Observable<Category[]> {
    return this.getStandardCategories().pipe(
      map((response: Category[]) => {
        return this.getCategoriesFilteredByType(response, categoryType);
      }),
    );
  }

  getCategoriesFilteredByType(categories: Category[], categoryType: string): Category[] {
    const segmentedCategoryTypes = CATEGORYTYPES.filter((object) => object.upa_uta === categoryType);
    return categories.filter((object) => {
      const matches = segmentedCategoryTypes.find(segmentedCategoryType => segmentedCategoryType.id === object.type_id);
      if (matches) {
        object['type'] = matches;
        return true;
      }
    });
  }

  getCustomCategoriesWithType(airlineId: any): Observable<Category[]> {
    return this.apiService.get(`${this.path}/airlines/${airlineId}`).pipe(
      map((response: any) => this.addTypeToCustomCategories(response.categories)),
    );
  }

  addTypeToCustomCategories(categories: Category[]): Category[] {
    return categories?.map(category => ({
      ...category,
      type: CATEGORYTYPES.find(c => c.id === 0),
      type_id: 0
    }));
  }

  getCategories(airlineId: any): Observable<Category[]> {
    if(!airlineId) {
      return of([]);
    }
    return this.apiService.get<Category[]>(`${this.path}/airlines/${airlineId}/categories`).pipe(
      map((categories: Category[]) => {
        if (!this.featureFlagsService.getFeatureFlag('bundlesTargeting')) {
          // Strip any seeded/leaked bundles category so it can't be selected while off.
          return categories.filter(c => c.id !== 'bundles');
        }
        return categories;
      }),
    );
  }

  createCategory(airlineId: string, data: Partial<Category>): Observable<Category> {
    return this.apiService.post(`${this.path}/airlines/${airlineId}/categories`, { data });
  }

  updateCategories(airlineId: string, data: any) {
    return this.apiService.put(`${this.path}/airlines/${airlineId}/categories`, { data });
  }

  updateCategoriesOrder(airlineId: number, categoryIds: string[]) {
    const data = {
      airline_id: airlineId,
      category_ids: categoryIds,
    };
    return this.apiService.post(`${this.path}/update_category_ordering`, { data });
  }
}
